import { CompoundProductionStepAssignmentDto, CompoundProductionStepDto, CompoundProductionStepType, PosologySliceDto, PosologySliceType, PrescriptionDto, ProductCatalogDto, ProductOrderDto, ProductPackageUom, ProductType } from '@services/dtos'
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { CompoundProductionStepTypeLabels, PosologyPatientTypeLabels, ProductOrderPaymentStatusLabels, ProductOrderPaymentStatusRender, ProductTypeLabels } from './constants'
import * as Icons from '@mui/icons-material'
import { ChipProductOrderStatus } from '@components/ChipProductOrderStatus'
import { Fragment } from 'react/jsx-runtime'
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch'
import moment from 'moment'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { ChipPrescriptionStatus } from '@components/ChipPrescriptionStatus'

export const compoundProductionStepColumns: GridColumns<CompoundProductionStepDto> = [
  { field: 'code', headerName: 'Code', width: 100 },
  { field: 'label', headerName: 'Libellé', width: 150 },
  {
    field: 'site',
    headerName: 'Site',
    valueGetter: (params) => params.row.site.label,
    width: 150,
  },
  {
    field: 'type',
    headerName: 'Type',
    width: 160,
    valueGetter: (params) => CompoundProductionStepTypeLabels[params.row.type],
  },
  {
    field: 'expectedResult',
    headerName: 'Valeur attendue',
    valueGetter: (params) => {
      switch (params.row.type) {
        case CompoundProductionStepType.BOOLEAN:
          return params.row.expected ? 'Oui' : 'Non'
        case CompoundProductionStepType.TEXT:
          return params.row.expectedValue ?? ''
        case CompoundProductionStepType.DILUTION:
          return params.row.dilutionVolumeMl ?? ''
        case CompoundProductionStepType.TIMER:
          return params.row.timerDurationSeconds ?? ''
        default:
          return ''
      }
    },
    width: 150,
  },
  {
    field: 'unit',
    headerName: 'Unité',
    width: 100,
    valueGetter: (params) =>
      params.row.type === CompoundProductionStepType.DILUTION
        ? 'mL'
        : (params.row.type === CompoundProductionStepType.TIMER
          ? 's'
          : (params.row.unit ?? '')),
  },
  {
    field: 'comment',
    headerName: 'Commentaire',
    width: 200,
    valueGetter: (params) => params.row.comment ?? '',
  },
]

export const compoundProductionStepAssignmentColumns: GridColumns<CompoundProductionStepAssignmentDto> = [
  {
    field: 'compound',
    headerName: 'MRP',
    width: 200,
    valueGetter: (params) => params.row.compound.name,
  },
  {
    field: 'steps',
    headerName: 'Etapes',
    width: 200,
    valueGetter: (params) =>
      params.row.steps.map(({ label }) => label).join(', '),
  },
]

export const productCatalogsColumns: GridColumns<ProductCatalogDto> = [
  { field: 'productCode', headerName: 'Code', width: 75 },
  {
    field: 'name',
    headerName: 'Nom',
    width: 220,
    renderCell: (params) => (
      <Typography
        variant="body2"
        fontWeight="bold"
      >
        {params.value}
      </Typography>
    )
  },
  { field: 'priceExclVat', headerName: 'Prix HT (€)', width: 90, renderCell: (params) => {
    const price = params.row.priceExclVat.toFixed(2)
    if (params.row.priceExpired) {
      return (
        <div className="flex items-center">
          <Tooltip title="Prix expiré" arrow>
            <WarningAmberIcon className="text-red-500 mr-1" fontSize='small' />
          </Tooltip>
          {price}
        </div>
      )
    }
    return price
  }},
  {
    field: 'vatRate',
    headerName: 'TVA (%)',
    width: 70,
    valueGetter: (params) => params.row.vatRate ? (params.row.vatRate * 100) : ''
  },
  { field: 'priceInclVat', headerName: 'Prix TTC (€)', width: 100, valueGetter: (params) => 
    !params.row.priceExclVat || !params.row.vatRate
      ? ''
      : (params.row.priceExclVat * (1 + params.row.vatRate)).toFixed(2) },
  {
    field: 'productFamily',
    headerName: 'Famille de produits',
    width: 180,
    renderCell: (params) => {
      const family = params.row.productFamily
      const iconColor = family?.color || 'grey'
      const IconComponent = Icons[family?.icon ?? 'Category' as keyof typeof Icons]

      return (
        <Box display="flex" alignItems="center" gap={1}>
          {IconComponent && <IconComponent style={{ color: iconColor, fontSize: 20 }} />}
          <Typography variant="body2">{family?.name ?? 'Aucune'}</Typography>
        </Box>
      )
    },
  },
  { field: 'type', headerName: 'Type', width: 120, valueGetter: (params) => ProductTypeLabels[params.row.type] },
  { field: 'genericName', headerName: 'DCI', width: 200 },
  { field: 'supplier', headerName: 'Fournisseur', width: 140, valueGetter: (params) => params.row.supplier?.name || '' },
  { field: 'supplyLeadTimeDays', headerName: 'Délai approvisionnement (j)', width: 60 },
  { field: 'maxDivision', headerName: 'Division maximale', width: 40 },
  { field: 'generatorParentIsotope', headerName: 'Isotope père', width: 100, valueGetter: (params) => params.row.generatorParentIsotope?.code || '' },
  { field: 'generatorDaughterIsotope', headerName: 'Isotope fils', width: 100, valueGetter: (params) => params.row.generatorDaughterIsotope?.code || '' },
  { field: 'compound', headerName: 'MRP', valueGetter: (params) => params.row.compound?.name || '' },
  { field: 'packageUom', headerName: 'Conditionnement', width: 140, valueGetter: (params) => 
    params.row.packageUom === ProductPackageUom.UNIT ? 'Unité / Dose' : 'Lot' },
  { field: 'unitsPerPack', headerName: 'Nb/lot', width: 65 },
  { field: 'gtinEan', headerName: 'GTIN/EAN', width: 170 },
  { field: 'requiresColdChain', headerName: 'Chaîne froid requise', width: 110, type: 'boolean' },
  { field: 'inUseStabilityMinCold', headerName: 'Stabilité min. froid', width: 150 },
  { field: 'inUseStabilityMinRoom', headerName: 'Stabilité min. ambiante', width: 170 },
  { field: 'excursionOutOfColdMaxMin', headerName: 'Excursion max. hors froid', width: 190 },
  { field: 'validation', headerName: 'Validation requise', width: 140, valueGetter: (params) => !!params.row.validation, type: 'boolean' },
  { field: 'orderableIfExamAssociated', headerName: 'Si exam', width: 80, type: 'boolean' },
  { field: 'exams', headerName: 'Examens', width: 200, valueGetter: (params) => params.row.exams?.map(exam => exam.label).join(', ') || '' },
  { field: 'notes', headerName: 'Notes', width: 200 },
]

export const productOrderCatalogsColumns = productCatalogsColumns.filter(column => 
  ![
    'supplier', 
    'requiresColdChain', 
    'inUseStabilityMinCold', 
    'inUseStabilityMinRoom', 
    'exams', 
    'notes', 
    'excursionOutOfColdMaxMin',
    'orderableIfExamAssociated'
  ].includes(column.field))

export const productOrdersColumns: GridColumns<ProductOrderDto> = [ 
  { field: 'status', headerName: 'Statut', width: 180, renderCell: (params) => <ChipProductOrderStatus status={params.value} /> },
  { field: 'paymentStatus', headerName: 'Paiement', width: 100, renderCell: (params) => {
    const status = params.row.paymentStatus
    if (!status) {
      return null
    }
    const { color, icon: Icon } = ProductOrderPaymentStatusRender[status]
    return (<Chip 
      label={ProductOrderPaymentStatusLabels[status]}
      size="small" icon={<Icon style={{ color, fontSize: 16 }} />} 
      style={{ borderColor: color, color }} variant="outlined" />)
  }},
  { field: 'internalOrderNumber', headerName: 'N° interne', width: 105 },
  { field: 'supplierOrderNumber', headerName: 'N° fournisseur', width: 120 },
  { field: 'createdAt', headerName: 'Date création', width: 110, valueGetter: (params) => new Date(params.row.createdAt).toLocaleDateString('fr-FR') },
  { field: 'amountExclVat', headerName: 'Montant HT (€)', width: 120, valueGetter: (params) => params.row.amountExclVat.toFixed(2) },
  { field: 'amountInclVat', headerName: 'Montant TTC (€)', width: 130, valueGetter: (params) => params.row.amountInclVat.toFixed(2) },
  { field: 'extraCost', headerName: 'Coût sup (€)', width: 105, valueGetter: (params) => (params.row.extraCost || 0).toFixed(2) },
  { field: 'supplier', headerName: 'Fournisseur', width: 135, valueGetter: (params) => `${params.row.supplier.name} <${params.row.supplierEmail}>` },
  { field: 'products', headerName: 'Produits', width: 400,
    renderCell: (params) => params.row.lineItems.map((item, index) => {
      const IconComponent = Icons[item.product.productFamily?.icon ?? 'Category' as keyof typeof Icons]
      const iconColor = item.product.productFamily?.color || 'grey'

      return (
        <Fragment key={item.product.id}>
          {index > 0 && <span className="mr-1">, </span>}
          <IconComponent className="mr-1" style={{ color: iconColor, fontSize: 14 }} />
          <span>{item.product.name} ({item.quantity} x {item.unitPriceExclVat.toFixed(2)} € HT / {(item.unitPriceExclVat * (1 + item.vatRate)).toFixed(2)} € TTC)</span>
        </Fragment>
      )
    })
  },
  { field: 'site', headerName: 'Site', width: 150, valueGetter: (params) => params.row.site.label },
  { field: 'shipping', headerName: 'Info livraison', width: 350,
    valueGetter: (params) => {
      const address = `${params.row.streetAddress}, ${params.row.postalCode} ${params.row.city}, ${params.row.country}`
      const numbers = [
        params.row.phoneNumber,
        params.row.fax
      ].filter(Boolean)
      return numbers.length > 0
        ? `${address} (${numbers.join(', ')})`
        : address
    }},
  { 
    field: 'desiredDeliveryDates', 
    headerName: 'Livraison souhaitée', 
    width: 300,
    valueGetter: (params) =>
      params.row.lineItems
        .filter(item => item.product.type !== ProductType.GENERATOR)
        .map(item =>
          `${item.product.name} : ${
            item.desiredDeliveryDate
              ? new Date(item.desiredDeliveryDate).toLocaleDateString('fr-FR')
              : 'Délai standard'
          }`
        )
        .join(', ')
  },
  {
    field: 'plannedDates',
    headerName: 'Dates prévisionnelles',
    width: 300,
    valueGetter: (params) =>
      params.row.lineItems
        .filter(({ product }) => product.type === ProductType.GENERATOR)
        .map(({ product, plannedDates }) =>
          `${product.name} : ${(plannedDates ?? []).map(({ date, quantity }) => 
            `${new Date(date).toLocaleDateString('fr-FR')} x${quantity}`).join(' - ')}`
        )
        .join(', ')
  },
  { field: 'deliveryInstructions', headerName: 'Instructions livraison', width: 220 },
  { field: 'gePurchaseOrderAttachments', headerName: 'Bons commande GE', width: 155, renderCell: (params) => (
    <>
      {params.row.lineItems.map(({ product, gePurchaseOrderAttachment }) => (
        gePurchaseOrderAttachment ? (
          <Tooltip key={`${product.id}-${gePurchaseOrderAttachment.id}`} title={`Ouvrir bon commande ${product.name}`}>
            <IconButton onClick={() => gePurchaseOrderAttachment && window.open(gePurchaseOrderAttachment.url, '_blank')}>
              <ContentPasteSearchIcon />
            </IconButton>
          </Tooltip>
        ) : null
      ))}
    </>
  ) },
  { field: 'sentToSupplierAt', headerName: 'Date email fournisseur', width: 140, valueGetter: (params) => 
    params.row.sentToSupplierAt ? moment(params.row.sentToSupplierAt).format('DD/MM/YYYY HH:mm') : '' },
  { field: 'createdBy', headerName: 'Créée par', width: 140, valueGetter: 
    (params) => `${params.row.createdBy?.firstName || ''} ${params.row.createdBy?.lastName || ''}` },
  { field: 'completedBy', headerName: 'Finalisée par', width: 140, valueGetter: 
    (params) => `${params.row.completedBy?.firstName || ''} ${params.row.completedBy?.lastName || ''}` },
]

export const productOrdersToValidateColumns = productOrdersColumns.filter(column => 
  !['status', 'sentToSupplierAt', 'completedBy', 'supplierOrderNumber', 'paymentStatus'].includes(column.field))

export const prescriptionsColumns: GridColumns<PrescriptionDto> = [
  { field: 'status', headerName: 'Statut', width: 150, renderCell: (params) => (
    <ChipPrescriptionStatus status={params.row.status} />
  )},
  { field: 'patient', headerName: 'Patient', width: 180, valueGetter: (params) => 
    `${params.row.appointment.patient?.firstName ?? ''} ${params.row.appointment.patient?.lastName ?? ''}` },
  { field: 'patientSsn', headerName: 'SSN', width: 155, valueGetter: (params) => 
    `${params.row.appointment.patient?.ssn ?? ''}` },
  { field: 'appointment', headerName: 'Date rdv', width: 155, valueGetter: (params) => 
    `${moment(params.row.appointment.start).format('DD/MM/YYYY HH:mm')}` },
  {
    field: 'exam',
    headerName: 'Examen',
    width: 180,
    renderCell: (params) => {
      return (
        <div className="flex items-center gap-1">
          {params.row.appointment.deletedAt && (
            <Chip
              label="Annulé"
              size="small"
              color="error"
              variant="outlined" />
          )}
          <span>{params.row.appointment.exam?.label ?? ''}</span>
        </div>
      )
    }
  }
]

export const posologyColumns: GridColumns<PosologySliceDto> = [
  {
    field: 'patientType',
    headerName: 'Type patient',
    width: 95,
    renderCell: ({ row }) => PosologyPatientTypeLabels[row.patientType]
  },
  {
    field: 'weightRange',
    headerName: 'Tranche',
    width: 120,
    renderCell: ({ row }) => {
      if (row.weightMin === null && row.weightMax !== null) return `< ${row.weightMax.toFixed(2)} kg`
      if (row.weightMin !== null && row.weightMax === null) return `≥ ${row.weightMin.toFixed(2)} kg`
      if (row.weightMin !== null && row.weightMax !== null) return `${row.weightMin.toFixed(2)} - ${row.weightMax.toFixed(2)} kg`
      return '—'
    },
  },
  {
    field: 'activityMBq',
    headerName: 'Activité',
    width: 95,
    renderCell: ({ value, row }) => {
      if (!value) {
        return '-'
      }
      return row.type === PosologySliceType.MBQ_PER_KG ? `${value.toFixed(2)} MBq/kg` : `${value.toFixed(2)} MBq`
    },
  },
  {
    field: 'eanm',
    headerName: 'EANM',
    flex: 1,
    renderCell: ({ row }) => row.type === PosologySliceType.EANM ? 
      `${row.eanmClass} - base ${row.eanmBaseActivityMBq?.toFixed(2)} MBq - min ${row.eanmMinActivityMBq?.toFixed(2)} MBq` 
      : '-'
  },
]
